import { AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createColumnHelper } from '@tanstack/react-table';
import { CircleMinus, Info, UserCheck } from 'lucide-react';
import { Trans } from 'react-i18next';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';
import {
  getMemberStatusText,
  getUtilizationColor,
  StatusBadge,
  StatusBadgeType,
} from '@/modules/common';
import { getServiceIcon } from '@/modules/onboarding/integrations-setup/helpers/data';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { getMembershipTypeLabel } from '@/modules/team/utils/membership-type.utils';
import { formatDisplayDate, formatMinutesToTimeString } from '@/utils/date-formatters';

import { MemberStatus, TeamMemberListItem } from '../types/team-member';

const columnHelper = createColumnHelper<TeamMemberListItem>();

const renderArchivedText = (status: MemberStatus, value?: string) => {
  const noValue = i18n.t('common:table.noValue');
  const displayValue = value || noValue;

  return (
    <ScrollableText
      className={cn('whitespace-nowrap', status === MemberStatus.Archived && 'opacity-50')}
    >
      {displayValue}
    </ScrollableText>
  );
};

export const getTeamTableColumns = () => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-3">
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectAll')}
        />
      </label>
    ),
    cell: ({ row }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-4">
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectRow')}
        />
      </label>
    ),
    enableSorting: false,
    enableColumnFilter: false,
    size: 40,
    minSize: 40,
    maxSize: 40,
  }),
  columnHelper.accessor('name', {
    header: i18n.t('authenticated.team:name'),
    cell: ({ row, getValue }) => (
      <ScrollableCellWithAvatar
        text={getValue() || i18n.t('common:table.noValue')}
        image={row.original?.avatarLink}
        avatarName={getValue()}
        className={cn(row.original.status === MemberStatus.Archived && 'opacity-50')}
      />
    ),
    size: 200,
    minSize: 150,
    enablePinning: true,
    enableHiding: false,
  }),
  columnHelper.accessor('email', {
    header: i18n.t('authenticated.team:email'),
    size: 300,
    minSize: 200,
    cell: ({ getValue, row }) => renderArchivedText(row.original.status, getValue() ?? undefined),
  }),
  columnHelper.accessor('status', {
    header: i18n.t('authenticated.team:status.label'),
    enableHiding: false,
    cell: ({ getValue, row }) => {
      const status = getValue();
      const teamMember = row.original;

      let warningTooltip: string | undefined;

      if (teamMember.nextArchiveDate) {
        warningTooltip = i18n.t('authenticated.team:scheduledArchive.willBeArchivedOn', {
          date: formatDisplayDate(teamMember.nextArchiveDate),
        });
      } else if (teamMember.nextUnarchiveDate) {
        warningTooltip = i18n.t('authenticated.team:scheduledArchive.willBeUnarchivedOn', {
          date: formatDisplayDate(teamMember.nextUnarchiveDate),
        });
      }

      return (
        <StatusBadge
          type={StatusBadgeType.TeamMember}
          status={status}
          label={getMemberStatusText(status)}
          warningTooltip={warningTooltip}
        />
      );
    },
    size: 200,
  }),
  columnHelper.accessor('department', {
    header: i18n.t('authenticated.team:department'),
    cell: ({ getValue, row }) => renderArchivedText(row.original.status, getValue()),
    size: 200,
    enableHiding: false,
  }),
  columnHelper.accessor('seniority', {
    header: i18n.t('authenticated.team:seniority.label'),
    cell: ({ getValue, row }) => renderArchivedText(row.original.status, getValue()),
    enableHiding: false,
  }),
  columnHelper.accessor('role', {
    header: i18n.t('authenticated.team:role'),
    cell: ({ getValue, row }) => renderArchivedText(row.original.status, getValue()),
  }),
  columnHelper.accessor('membershipType', {
    header: i18n.t('authenticated.team:membershipType'),
    cell: ({ getValue, row }) => {
      const value = getValue();
      const label = value ? getMembershipTypeLabel(value) : undefined;
      return renderArchivedText(row.original.status, label);
    },
  }),
  columnHelper.accessor('manager', {
    header: i18n.t('authenticated.team:manager'),
    cell: ({ getValue, row }) => {
      const manager = getValue();
      const isArchived = row.original.status === MemberStatus.Archived;

      if (!manager) {
        return renderArchivedText(row.original.status, undefined);
      }

      return (
        <ScrollableCellWithAvatar
          text={manager.name}
          image={manager.profileImageUrl}
          avatarName={manager.name}
          className={cn(isArchived && 'opacity-50')}
        />
      );
    },
  }),
  columnHelper.accessor('isManager', {
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.team:details.tabs.info.isManager')}
      </div>
    ),
    meta: { cellClassName: 'text-center' },
    cell: ({ getValue, row }) => {
      const isManager = getValue();
      const isArchived = row.original.status === MemberStatus.Archived;

      return (
        <div className={cn('flex items-center justify-center', isArchived && 'opacity-50')}>
          {isManager ? (
            <UserCheck className="h-5 w-5 text-success" />
          ) : (
            <CircleMinus className="h-5 w-5 text-muted-foreground/40" />
          )}
        </div>
      );
    },
    size: 100,
  }),
  columnHelper.display({
    id: 'location',
    header: i18n.t('authenticated.team:location'),
    cell: ({ row }) => {
      const { country, city } = row.original;
      const parts = [country, city].filter(Boolean);
      const location = parts.length > 0 ? parts.join(', ') : undefined;
      return renderArchivedText(row.original.status, location);
    },
  }),
  columnHelper.accessor('capacity', {
    header: i18n.t('authenticated.team:capacity'),
    size: 100,
    minSize: 75,
    maxSize: 150,
    cell: ({ getValue, row }) => {
      const capacityMinutes = getValue();
      const isArchived = row.original.status === MemberStatus.Archived;
      const isOnDemand = row.original.isOnDemandAvailability;

      // Show "On Demand" with info icon and tooltip if isOnDemandAvailability is true
      if (isOnDemand) {
        return (
          <SimpleTooltip
            content={i18n.t('authenticated.team:details.tabs.capacity.onDemandTooltip')}
            contentClassName="max-w-xs text-sm"
          >
            <div className={cn('flex items-center gap-1', isArchived && 'opacity-50')}>
              <Info size={14} className="text-muted-foreground" />
              <p className="whitespace-nowrap">{i18n.t('authenticated.team:onDemand')}</p>
            </div>
          </SimpleTooltip>
        );
      }

      if (capacityMinutes === null || capacityMinutes === undefined) {
        return <p className={cn(isArchived && 'opacity-50')}>{i18n.t('common:table.noValue')}</p>;
      }

      const formattedCapacity = formatMinutesToTimeString(capacityMinutes);

      return (
        <p className={cn('whitespace-nowrap', isArchived && 'opacity-50')}>{formattedCapacity}</p>
      );
    },
  }),
  columnHelper.display({
    id: 'billableCapacity',
    size: 200,
    minSize: 200,
    maxSize: 300,
    header: i18n.t('authenticated.team:conflictFields.capacity.billableCapacity'),
    cell: ({ row }) => {
      const capacityMinutes = row.original.capacity;
      const billablePercent = row.original.billableCapacity;
      const isArchived = row.original.status === MemberStatus.Archived;
      const isOnDemand = row.original.isOnDemandAvailability;

      if (isOnDemand) {
        return (
          <p className={cn('whitespace-nowrap', isArchived && 'opacity-50')}>
            {i18n.t('common:table.noValue')}
          </p>
        );
      }

      if (
        capacityMinutes === null ||
        capacityMinutes === undefined ||
        billablePercent === null ||
        billablePercent === undefined
      ) {
        return <p className={cn(isArchived && 'opacity-50')}>{i18n.t('common:table.noValue')}</p>;
      }

      // billableCapacity is a percentage (0-100), calculate billable minutes then percentage of 8h day
      const billableMinutes = capacityMinutes * (billablePercent / 100);
      const billableUtilization = Math.round(billablePercent); // 480 = 8h * 60min

      // Calculate hours for display
      const billableCapacityHours = formatMinutesToTimeString(billableMinutes);
      const totalCapacityHours = formatMinutesToTimeString(capacityMinutes);

      return (
        <SimpleTooltip
          content={
            <Trans
              i18nKey="authenticated.team:conflictFields.capacity.billableCapacityTooltip"
              components={{
                bold: <b />,
              }}
              values={{
                billableCapacity: `${billableCapacityHours}`,
                capacity: `${totalCapacityHours}`,
              }}
            />
          }
        >
          <div className={cn('flex items-center gap-2', isArchived && 'opacity-50')}>
            <Progress
              value={billableUtilization}
              className="w-[100px]"
              color="secondary"
              indicatorColor={getUtilizationColor(billableUtilization)}
            />
            <p>{i18n.t('common:percent', { value: billableUtilization })}</p>
          </div>
        </SimpleTooltip>
      );
    },
  }),
  /* TODO Revert when Tags should become supported. Currently they are not supported in MVP*/
  /*columnHelper.accessor('tags', {
    header: i18n.t('authenticated.team:tags'),
    cell: ({ getValue }) => {
      const tags = getValue() || [];
      const maxTags = 2;
      const itemsLeft = tags.length ? tags.length - maxTags : 0;
      return (
        <div className="flex gap-2">
          {tags.slice(0, maxTags).map(tag => (
            <Badge key={tag} variant="secondary" className="whitespace-nowrap">
              {tag}
            </Badge>
          ))}
          {itemsLeft > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="secondary">{`+${itemsLeft}`}</Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tags.slice(maxTags).join(', ')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  }),*/
  columnHelper.accessor('connectedIntegrations', {
    header: i18n.t('authenticated.team:integrations'),
    cell: ({ getValue, row }) => {
      const integrations = getValue() || [];

      const isArchived = row.original.status === MemberStatus.Archived;

      if (integrations.length === 0) {
        return renderArchivedText(
          row.original.status,
          i18n.t('authenticated.team:table.noConnections')
        );
      }

      return (
        <div
          className={cn(
            'flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale',
            isArchived && 'opacity-50'
          )}
        >
          {integrations.map(integration => (
            <SimpleTooltip key={integration} content={integration}>
              <Avatar slot="avatar" className="flex h-6 w-6 items-center justify-center bg-muted">
                <AvatarImage
                  src={getServiceIcon(integration)}
                  alt={integration}
                  className="h-4 w-4"
                />
                <AvatarFallback>{integration}</AvatarFallback>
              </Avatar>
            </SimpleTooltip>
          ))}
        </div>
      );
    },
  }),
];
